import React, { useState, useContext, useEffect } from 'react';
import { fmtDateTime as _fmtDateTime } from '@/lib/dates';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, AlertTriangle, Download } from 'lucide-react';

const IncidentReportDetail = () => {
  const { reportId } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/incident-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load incident report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'Low': 'bg-green-100 text-green-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-orange-100 text-orange-800',
      'Critical': 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">Loading...</div>;
  }

  if (!report) {
    return <div className="flex items-center justify-center py-12">Report not found</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/field/incident-reports')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">{report.incident_type}</h1>
            <p className="text-gray-600">Report #: {report.report_number}</p>
          </div>
          <Badge className={getSeverityColor(report.severity)}>
            {report.severity}
          </Badge>
        </div>
      </div>

      {/* Basic Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Incident Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p className="font-medium">{report.location}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Incident Date & Time</p>
              <p className="font-medium">{_fmtDateTime(report.incident_date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Reported By</p>
              <p className="font-medium">{report.reporter_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Reporter Position</p>
              <p className="font-medium">{report.reporter_position}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Contact Information</p>
              <p className="font-medium">{report.reporter_contact}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Submitted</p>
              <p className="font-medium">{_fmtDateTime(report.submitted_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Description */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Incident Description</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 whitespace-pre-wrap">{report.description}</p>
        </CardContent>
      </Card>

      {/* Injuries */}
      {report.injuries_reported && (
        <Card className="mb-6 border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600">Injuries Reported</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant="destructive" className="mb-2">INJURIES INVOLVED</Badge>
            <p className="text-gray-700 whitespace-pre-wrap">{report.injury_details}</p>
          </CardContent>
        </Card>
      )}

      {/* Witnesses */}
      {report.witnesses && report.witnesses.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Witnesses ({report.witnesses.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {report.witnesses.map((witness, idx) => (
                <div key={idx} className="border-b pb-3 last:border-b-0">
                  <p className="font-medium">{witness.name}</p>
                  <p className="text-sm text-gray-600">{witness.contact}</p>
                  {witness.statement && (
                    <p className="text-sm text-gray-700 mt-2 italic">"{witness.statement}"</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Immediate Actions */}
      {report.immediate_action_taken && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Immediate Actions Taken</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{report.immediate_action_taken}</p>
          </CardContent>
        </Card>
      )}

      {/* Photos */}
      {report.photos && report.photos.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Photos ({report.photos.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {report.photos.map((photo, idx) => (
                <div key={idx} className="border rounded-lg p-2">
                  <img src={photo} alt={`Incident photo ${idx + 1}`} className="w-full h-40 object-cover rounded" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={() => navigate('/field/incident-reports')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <Button>
          <Download className="h-4 w-4 mr-2" />
          Download PDF
        </Button>
      </div>
    </div>
  );
};

export default IncidentReportDetail;
